import { useState, useMemo, useRef, type ReactNode } from "react";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import Papa from "papaparse";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, parseFieldErrors } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  insertFurnishingCatalogItemSchema,
  insertFurnishingPackageSchema,
  FURNISHING_ROOM_TYPES,
  FURNISHING_ITEM_CATEGORIES,
  type FurnishingCatalogItem,
  type FurnishingPackage,
  type FurnishingPackageItem,
} from "@shared/schema";

type FurnishingCatalogItemWithCreator = FurnishingCatalogItem & { createdByName?: string | null };
type FurnishingPackageWithCreator = FurnishingPackage & {
  createdByName?: string | null;
  items?: FurnishingPackageItem[];
};
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Edit, Trash2, ExternalLink, Search, BookOpen, Package, ChevronDown, ChevronUp, Download, FileDown, Upload, Loader2, Pencil, Archive, ArchiveRestore, Lock, ShoppingCart } from "lucide-react";
import { Link } from "wouter";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";

const CATEGORY_LABELS: Record<string, string> = {
  furniture: "Furniture",
  appliance: "Appliance",
  decor: "Decor",
  electronics_smart_home: "Electronics & Smart Home",
  safety_security: "Safety & Security",
  cleaning_maintenance: "Cleaning & Maintenance",
  tools_supplies: "Tools & Supplies",
  storage_organization: "Storage & Organization",
};

const CATEGORY_COLORS: Record<string, string> = {
  furniture: "bg-blue-100 text-blue-800",
  appliance: "bg-red-100 text-red-800",
  decor: "bg-pink-100 text-pink-800",
  electronics_smart_home: "bg-indigo-100 text-indigo-800",
  safety_security: "bg-yellow-100 text-yellow-800",
  cleaning_maintenance: "bg-teal-100 text-teal-800",
  tools_supplies: "bg-orange-100 text-orange-800",
  storage_organization: "bg-violet-100 text-violet-800",
};

const ROOM_TYPE_LABELS: Record<string, string> = {
  bedroom: "Bedroom",
  bathroom: "Bathroom",
  common_space: "Common Space",
  kitchen: "Kitchen",
  appliances: "Appliances",
  other: "Other",
};

function fmtCurrency(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function parsePrice(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return isFinite(n) ? n : null;
}

const ROOM_TYPE_COLORS: Record<string, string> = {
  bedroom: "bg-blue-100 text-blue-800",
  bathroom: "bg-purple-100 text-purple-800",
  common_space: "bg-green-100 text-green-800",
  kitchen: "bg-orange-100 text-orange-800",
  appliances: "bg-red-100 text-red-800",
  other: "bg-gray-100 text-gray-800",
};

const CSV_HEADERS = ["name", "room_type", "category", "description", "price", "purchase_url"] as const;

function escapeCsvCell(value: string | null | undefined): string {
  const str = value ?? "";
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsvString(rows: string[][]): string {
  return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\n");
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function parseCsvText(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const result = Papa.parse<Record<string, string>>(text.trim(), {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim().toLowerCase(),
  });
  return { headers: result.meta.fields ?? [], rows: result.data };
}

interface ParsedRow {
  rowNumber: number;
  raw: Record<string, string>;
  normalized: {
    name: string;
    roomType: string;
    category: string;
    description: string;
    price: string;
    purchaseUrl: string;
  };
  errors: Record<string, string>;
  isDuplicateOfExisting: boolean;
  isDuplicateInFile: boolean;
}

export type ImportRowBadge = "new" | "willUpdate" | "duplicate" | "error";

/**
 * Pure classification used by the CSV import preview to choose the row's
 * badge ("Ready" / "Will update" / "Duplicate" / "Error"). Exported so the
 * insert-vs-upsert toggle behavior can be unit-tested without rendering the
 * whole page (Task #1399).
 */
export function classifyImportRow(
  row: { isDuplicateInFile: boolean; isDuplicateOfExisting: boolean; errors: Record<string, string> },
  importMode: "insert" | "upsert",
): ImportRowBadge {
  const hasError = Object.keys(row.errors).length > 0;
  if (hasError) return "error";
  const isDupInFile = row.isDuplicateInFile;
  const isDupOfExisting = row.isDuplicateOfExisting && !row.isDuplicateInFile;
  if (isDupOfExisting && importMode === "upsert") return "willUpdate";
  if (isDupInFile || (isDupOfExisting && importMode === "insert")) return "duplicate";
  return "new";
}

function validateCsvRows(
  headers: string[],
  rows: Record<string, string>[],
  existingItems: FurnishingCatalogItem[]
): ParsedRow[] {
  const existingNames = new Set(existingItems.map((i) => i.name.trim().toLowerCase()));
  const seenInFile = new Set<string>();

  const getCell = (row: Record<string, string>, col: string) => (row[col] ?? "").trim();

  return rows.map((row, idx) => {
    const raw: Record<string, string> = {};
    for (const h of headers) raw[h] = getCell(row, h);

    const name = getCell(row, "name");
    const room_type = getCell(row, "room_type");
    const category = getCell(row, "category");
    const description = getCell(row, "description");
    const price = getCell(row, "price");
    const purchase_url = getCell(row, "purchase_url");

    const errors: Record<string, string> = {};

    if (!name) {
      errors["name"] = "Name is required";
    }

    let normalizedRoomType = room_type || "other";
    if (room_type && !(FURNISHING_ROOM_TYPES as readonly string[]).includes(room_type)) {
      errors["room_type"] = `room_type must be one of: ${FURNISHING_ROOM_TYPES.join(", ")}`;
      normalizedRoomType = room_type;
    } else if (!room_type) {
      normalizedRoomType = "other";
    }

    let normalizedCategory = category || "furniture";
    if (category && !(FURNISHING_ITEM_CATEGORIES as readonly string[]).includes(category)) {
      errors["category"] = `category must be one of: ${FURNISHING_ITEM_CATEGORIES.join(", ")}`;
      normalizedCategory = category;
    } else if (!category) {
      normalizedCategory = "furniture";
    }

    let normalizedPrice = price;
    if (price) {
      const cleaned = price.replace(/^\$/, "").replace(/,/g, "");
      const isValid = /^\d+(\.\d+)?$/.test(cleaned) && isFinite(Number(cleaned)) && Number(cleaned) >= 0;
      if (!isValid) {
        errors["price"] = "price must be a number";
      } else {
        normalizedPrice = cleaned;
      }
    }

    let normalizedUrl = purchase_url;
    if (purchase_url) {
      try {
        const parsed = new URL(purchase_url);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          errors["purchase_url"] = "purchase_url must be a valid URL";
        }
      } catch {
        errors["purchase_url"] = "purchase_url must be a valid URL";
      }
    }

    const nameLower = name.trim().toLowerCase();
    const isDuplicateOfExisting = !!name && existingNames.has(nameLower);
    let isDuplicateInFile = false;
    if (name && !isDuplicateOfExisting) {
      if (seenInFile.has(nameLower)) {
        isDuplicateInFile = true;
      } else {
        seenInFile.add(nameLower);
      }
    } else if (name) {
      seenInFile.add(nameLower);
    }

    return {
      rowNumber: idx + 1,
      raw,
      normalized: {
        name,
        roomType: normalizedRoomType,
        category: normalizedCategory,
        description,
        price: normalizedPrice,
        purchaseUrl: normalizedUrl,
      },
      errors,
      isDuplicateOfExisting,
      isDuplicateInFile,
    };
  });
}

const catalogFormSchema = insertFurnishingCatalogItemSchema.omit({ userId: true });
type CatalogFormData = z.infer<typeof catalogFormSchema>;

const packageFormSchema = insertFurnishingPackageSchema.omit({ userId: true });
type PackageFormData = z.infer<typeof packageFormSchema>;

export default function AppFurnishingCatalog() {
  const queryClient = useQueryClient();
  usePageTitle("Furnishing Catalog");
  const { toast } = useToast();
  const { canEdit: canEditArea } = usePermissions();
  const canEditFurnishing = canEditArea("furnishing");
  const readOnly = !canEditFurnishing;
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "catalog", {
    tabs: ["catalog", "packages"],
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FurnishingCatalogItem | null>(null);
  const [roomTypeFilter, setRoomTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchText, setSearchText] = useState("");

  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<FurnishingPackage | null>(null);
  const [expandedPackageId, setExpandedPackageId] = useState<number | null>(null);
  const [addItemToPackageId, setAddItemToPackageId] = useState<number | null>(null);
  const [addItemCatalogId, setAddItemCatalogId] = useState<string>("");
  const [addItemQty, setAddItemQty] = useState<number>(1);

  const [showArchived, setShowArchived] = useState(false);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importPhase, setImportPhase] = useState<"preview" | "summary">("preview");
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importMode, setImportMode] = useState<"insert" | "upsert">("insert");
  const [isValidatingCsv, setIsValidatingCsv] = useState(false);
  const [importSummary, setImportSummary] = useState<{
    inserted: number;
    updated: number;
    skipped: { row: number; name: string; reason: string }[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const catalogQuery = useQuery<FurnishingCatalogItemWithCreator[]>({
    queryKey: ["/api/app/furnishing-catalog", { includeArchived: showArchived }],
    queryFn: async () => {
      const res = await fetch(`/api/app/furnishing-catalog?includeArchived=${showArchived}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load catalog");
      return res.json();
    },
  });

  const packagesQuery = useQuery<FurnishingPackageWithCreator[]>({
    queryKey: ["/api/app/furnishing-packages"],
  });

  const items = catalogQuery.data || [];
  const packages = packagesQuery.data || [];

  const filteredItems = useMemo(() => {
    let result = items;
    if (roomTypeFilter !== "all") result = result.filter((i) => i.roomType === roomTypeFilter);
    if (categoryFilter !== "all") result = result.filter((i) => i.category === categoryFilter);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter((i) => String(i.name ?? "").toLowerCase().includes(q) || String(i.description ?? "").toLowerCase().includes(q));
    }
    return result;
  }, [items, roomTypeFilter, categoryFilter, searchText]);

  const form = useForm<CatalogFormData>({
    resolver: zodResolver(catalogFormSchema),
    defaultValues: { name: "", description: null, roomType: "bedroom", category: "furniture", purchaseUrl: null, defaultPrice: null },
  });

  const packageForm = useForm<PackageFormData>({
    resolver: zodResolver(packageFormSchema),
    defaultValues: { name: "", roomType: "bedroom" },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CatalogFormData) => {
      const res = await apiRequest("POST", "/api/app/furnishing-catalog", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-catalog"] });
      setDialogOpen(false);
      form.reset();
      toast({ title: "Item added to catalog" });
    },
    onError: (err) => {
      const fieldErrors = parseFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) {
        for (const [field, message] of Object.entries(fieldErrors)) {
          form.setError(field as keyof CatalogFormData, { message });
        }
      } else {
        toast({ title: "Failed to add item", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CatalogFormData> }) => {
      const res = await apiRequest("PATCH", `/api/app/furnishing-catalog/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-catalog"] });
      setDialogOpen(false);
      setEditingItem(null);
      form.reset();
      toast({ title: "Item updated" });
    },
    onError: (err) => {
      const fieldErrors = parseFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) {
        for (const [field, message] of Object.entries(fieldErrors)) {
          form.setError(field as keyof CatalogFormData, { message });
        }
      } else {
        toast({ title: "Failed to update item", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/furnishing-catalog/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-catalog"] });
      toast({ title: "Item removed" });
    },
    onError: (err) => toast({ title: "Failed to delete item", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/app/furnishing-catalog/${id}/archive`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-catalog"] });
      toast({ title: "Item archived" });
    },
    onError: (err) => toast({ title: "Failed to archive item", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const unarchiveMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/app/furnishing-catalog/${id}/unarchive`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-catalog"] });
      toast({ title: "Item restored" });
    },
    onError: (err) => toast({ title: "Failed to restore item", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const createPackageMutation = useMutation({
    mutationFn: async (data: PackageFormData) => {
      const res = await apiRequest("POST", "/api/app/furnishing-packages", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-packages"] });
      setPackageDialogOpen(false);
      packageForm.reset();
      toast({ title: "Package created" });
    },
    onError: (err) => toast({ title: "Failed to create package", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const updatePackageMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<PackageFormData> }) => {
      const res = await apiRequest("PATCH", `/api/app/furnishing-packages/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-packages"] });
      setPackageDialogOpen(false);
      setEditingPackage(null);
      packageForm.reset();
      toast({ title: "Package updated" });
    },
    onError: (err) => toast({ title: "Failed to update package", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const deletePackageMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/furnishing-packages/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-packages"] });
      toast({ title: "Package deleted" });
    },
    onError: (err) => toast({ title: "Failed to delete package", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const bulkImportMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ rows, mode }: { rows: ParsedRow[]; mode: "insert" | "upsert" }) => {
      const sendableRows = rows.filter((r) => {
        if (Object.keys(r.errors).length > 0) return false;
        if (r.isDuplicateInFile) return false;
        if (mode === "insert" && r.isDuplicateOfExisting) return false;
        return true;
      });
      const itemsPayload = sendableRows.map((r) => ({
        rowNumber: r.rowNumber,
        name: r.normalized.name,
        roomType: r.normalized.roomType,
        category: r.normalized.category,
        description: r.normalized.description || null,
        defaultPrice: r.normalized.price || null,
        purchaseUrl: r.normalized.purchaseUrl || null,
      }));
      const res = await apiRequest("POST", "/api/app/furnishing-catalog/bulk", { items: itemsPayload, mode });
      return res.json() as Promise<{ inserted: FurnishingCatalogItem[]; updated: FurnishingCatalogItem[]; skipped: { row: number; name: string; reason: string }[] }>;
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-catalog"] });
      const { rows, mode } = vars;
      const clientSkipped: { row: number; name: string; reason: string }[] = [];
      for (const r of rows) {
        if (Object.keys(r.errors).length > 0) {
          const reasons = Object.values(r.errors).join("; ");
          clientSkipped.push({ row: r.rowNumber, name: r.normalized.name, reason: reasons });
        } else if (r.isDuplicateInFile) {
          clientSkipped.push({ row: r.rowNumber, name: r.normalized.name, reason: "Duplicate — item already exists" });
        } else if (mode === "insert" && r.isDuplicateOfExisting) {
          clientSkipped.push({ row: r.rowNumber, name: r.normalized.name, reason: "Duplicate — item already exists" });
        }
      }
      const allSkipped = [...clientSkipped, ...data.skipped];
      const updatedCount = data.updated?.length ?? 0;
      setImportSummary({ inserted: data.inserted.length, updated: updatedCount, skipped: allSkipped });
      setImportPhase("summary");
      const parts: string[] = [];
      if (data.inserted.length > 0) parts.push(`${data.inserted.length} inserted`);
      if (updatedCount > 0) parts.push(`${updatedCount} updated`);
      if (parts.length === 0) parts.push("0 imported");
      toast({ title: `Import complete: ${parts.join(", ")}` });
    },
    onError: (err) => {
      toast({ title: "Import failed", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    },
  });

  const openCreate = () => {
    setEditingItem(null);
    form.reset({ name: "", description: null, roomType: "bedroom", category: "furniture", purchaseUrl: null, defaultPrice: null });
    setDialogOpen(true);
  };

  const openEdit = (item: FurnishingCatalogItem) => {
    setEditingItem(item);
    form.reset({
      name: item.name,
      description: item.description ?? null,
      roomType: item.roomType as typeof FURNISHING_ROOM_TYPES[number],
      category: (item.category || "furniture") as typeof FURNISHING_ITEM_CATEGORIES[number],
      purchaseUrl: item.purchaseUrl ?? null,
      defaultPrice: item.defaultPrice ?? null,
    });
    setDialogOpen(true);
  };

  const onSubmit = (data: CatalogFormData) => {
    const normalized: CatalogFormData = {
      ...data,
      description: data.description?.trim() || null,
      purchaseUrl: data.purchaseUrl?.trim() || null,
      defaultPrice: data.defaultPrice?.trim() || null,
    };
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: normalized });
    } else {
      createMutation.mutate(normalized);
    }
  };

  const onPackageSubmit = (data: PackageFormData) => {
    if (editingPackage) {
      updatePackageMutation.mutate({ id: editingPackage.id, data });
    } else {
      createPackageMutation.mutate(data);
    }
  };

  const handleExportTemplate = () => {
    const content = buildCsvString([CSV_HEADERS as unknown as string[]]);
    downloadCsv("furnishings-catalog-template.csv", content);
  };

  const handleExportAll = () => {
    const rows: string[][] = [CSV_HEADERS as unknown as string[]];
    for (const item of items) {
      rows.push([
        item.name,
        item.roomType,
        item.category,
        item.description ?? "",
        item.defaultPrice ?? "",
        item.purchaseUrl ?? "",
      ]);
    }
    downloadCsv("furnishings-catalog.csv", buildCsvString(rows));
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target?.result as string;
      const { headers, rows } = parseCsvText(text);
      setParsedRows([]);
      setImportPhase("preview");
      setImportSummary(null);
      setImportMode("insert");
      setIsValidatingCsv(true);
      setImportModalOpen(true);
      let freshItems: FurnishingCatalogItem[] = items;
      try {
        try {
          await queryClient.refetchQueries({
            queryKey: ["/api/app/furnishing-catalog"],
          });
        } catch {
          // fall back to whatever we already had cached
        }
        const allCachedEntries = queryClient.getQueriesData<FurnishingCatalogItem[]>({
          queryKey: ["/api/app/furnishing-catalog"],
        });
        const activeEntry = allCachedEntries.find(([, d]) => Array.isArray(d) && d.length > 0);
        const fetched = activeEntry ? activeEntry[1] : undefined;
        if (Array.isArray(fetched)) freshItems = fetched;
        const validated = validateCsvRows(headers, rows, freshItems);
        setParsedRows(validated);
      } finally {
        setIsValidatingCsv(false);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const newRowCount = parsedRows.filter((r) => Object.keys(r.errors).length === 0 && !r.isDuplicateOfExisting && !r.isDuplicateInFile).length;
  const matchingExistingCount = parsedRows.filter((r) => Object.keys(r.errors).length === 0 && r.isDuplicateOfExisting && !r.isDuplicateInFile).length;
  const errorRowCount = parsedRows.filter((r) => Object.keys(r.errors).length > 0).length;
  const duplicateInFileCount = parsedRows.filter((r) => r.isDuplicateInFile && Object.keys(r.errors).length === 0).length;
  const importableCount = importMode === "upsert" ? newRowCount + matchingExistingCount : newRowCount;

  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="furnishing" />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-furnishing-catalog-title" data-tour="furnishing-heading">Furnishing Catalog</h1>
            <p className="text-sm text-gray-500 mt-1">Global catalog of furnishing items and packages for property launches</p>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/app/furnishing-address-book">
              <Button variant="outline" data-testid="button-open-address-book">
                <BookOpen className="h-4 w-4 mr-1.5" />
                Address Book
              </Button>
            </Link>
            {activeTab === "catalog" ? (
              <>
                {!readOnly && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleExportTemplate} data-testid="button-export-template">
                      <FileDown className="h-4 w-4 mr-1.5" />
                      Export template
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportAll} data-testid="button-export-all">
                      <Download className="h-4 w-4 mr-1.5" />
                      Export all items
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleImportClick} data-testid="button-import-csv">
                      <Upload className="h-4 w-4 mr-1.5" />
                      Import from CSV
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      data-testid="input-csv-file"
                      onChange={handleFileSelect}
                    />
                    <Button onClick={openCreate} data-testid="button-add-catalog-item" data-tour="furnishing-catalog-add">
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add Item
                    </Button>
                  </>
                )}
              </>
            ) : (
              canEditFurnishing && (
                <Button onClick={() => { setEditingPackage(null); packageForm.reset({ name: "", roomType: "bedroom" }); setPackageDialogOpen(true); }} data-testid="button-add-package">
                  <Plus className="h-4 w-4 mr-1.5" />
                  New Package
                </Button>
              )
            )}
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="furnishing" />
              <GuideButton pageKey="furnishing" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="furnishing" />
        </div>

        <div className="flex gap-4 border-b mb-6">
          <button
            className={`pb-3 text-sm font-medium border-b-2 ${activeTab === "catalog" ? "border-brand text-brand" : "border-transparent text-gray-500 hover:text-gray-700"}`}
            onClick={() => setActiveTab("catalog")}
            data-testid="tab-catalog-items"
          >
            Catalog Items ({items.length})
          </button>
          <button
            className={`pb-3 text-sm font-medium border-b-2 ${activeTab === "packages" ? "border-brand text-brand" : "border-transparent text-gray-500 hover:text-gray-700"}`}
            onClick={() => setActiveTab("packages")}
            data-testid="tab-catalog-packages"
          >
            Packages ({packages.length})
          </button>
        </div>

        {activeTab === "catalog" && (
          <>
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <Select value={roomTypeFilter} onValueChange={setRoomTypeFilter}>
                <SelectTrigger className="w-[180px] h-9 border-gray-300" data-testid="select-room-type-filter">
                  <SelectValue placeholder="Room Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Room Types</SelectItem>
                  {FURNISHING_ROOM_TYPES.map((rt) => (
                    <SelectItem key={rt} value={rt}>{ROOM_TYPE_LABELS[rt]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[160px] h-9 border-gray-300" data-testid="select-category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {FURNISHING_ITEM_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search catalog..."
                  className="w-[220px] h-9 border-gray-300 pl-9"
                  data-testid="input-catalog-search"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer ml-auto" data-testid="label-show-archived">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="rounded"
                  data-testid="toggle-show-archived"
                />
                Show archived
              </label>
            </div>

            {catalogQuery.isLoading ? (
              <div className="text-center py-12 text-gray-400">Loading catalog...</div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No catalog items found</p>
                <p className="text-sm text-gray-400 mt-1">Add items to build your reusable furnishing catalog</p>
                <Button className="mt-4" onClick={openCreate} data-testid="button-add-first-catalog-item">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add First Item
                </Button>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden" data-testid="table-furnishing-catalog" data-tour="furnishing-catalog-list">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium text-gray-600">Name</th>
                      <th className="text-left p-3 font-medium text-gray-600">Room Type</th>
                      <th className="text-left p-3 font-medium text-gray-600">Category</th>
                      <th className="text-left p-3 font-medium text-gray-600">Description</th>
                      <th className="text-left p-3 font-medium text-gray-600">Default Price</th>
                      <th className="text-left p-3 font-medium text-gray-600">Purchase Link</th>
                      <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => {
                      const isArchived = !!item.archivedAt;
                      const linkToShow = item.isSeeded ? (item.affiliateLink || item.purchaseUrl) : item.purchaseUrl;
                      return (
                      <tr key={item.id} className={`border-b last:border-0 hover:bg-gray-50 ${isArchived ? "opacity-60" : ""}`} data-testid={`row-catalog-item-${item.id}`}>
                        <td className="p-3 font-medium text-gray-900">
                          <div className="flex items-center gap-1.5">
                            {item.name}
                            {item.isSeeded && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-gray-400" title="PadPulse recommended item" data-testid={`badge-seeded-${item.id}`}>
                                <Lock className="h-3 w-3" />
                              </span>
                            )}
                            {isArchived && (
                              <Badge className="bg-gray-100 text-gray-500 text-xs ml-1" variant="secondary" data-testid={`badge-archived-${item.id}`}>Archived</Badge>
                            )}
                          </div>
                          <div className="text-xs font-normal text-gray-500 mt-0.5" data-testid={`text-catalog-added-by-${item.id}`}>
                            Added by {item.createdByName || "Unknown"}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className={ROOM_TYPE_COLORS[item.roomType] || "bg-gray-100 text-gray-800"} variant="secondary">
                            {ROOM_TYPE_LABELS[item.roomType] || item.roomType}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge className={CATEGORY_COLORS[item.category] || "bg-gray-100 text-gray-800"} variant="secondary">
                            {CATEGORY_LABELS[item.category] || item.category}
                          </Badge>
                        </td>
                        <td className="p-3 text-gray-500 max-w-xs truncate">{item.description || "—"}</td>
                        <td className="p-3 text-gray-700">{item.defaultPrice ? `$${item.defaultPrice}` : "—"}</td>
                        <td className="p-3">
                          {linkToShow ? (
                            // Icon-only on purpose: whether the link carries an affiliate
                            // tag is an implementation detail hosts don't need called out.
                            <a href={linkToShow} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 inline-flex items-center" title="Open purchase link" aria-label="Open purchase link" data-testid={`link-purchase-${item.id}`}>
                              <ShoppingCart className="h-4 w-4" />
                            </a>
                          ) : "—"}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {!isArchived && (
                              <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`button-edit-catalog-${item.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {isArchived ? (
                              <Button size="sm" variant="ghost" className="text-gray-500 hover:text-gray-700" onClick={() => unarchiveMutation.mutate(item.id)} data-testid={`button-restore-catalog-${item.id}`} title="Restore item">
                                <ArchiveRestore className="h-4 w-4" />
                              </Button>
                            ) : item.isSeeded ? (
                              <Button size="sm" variant="ghost" className="text-amber-600 hover:text-amber-800" onClick={() => archiveMutation.mutate(item.id)} data-testid={`button-archive-catalog-${item.id}`} title="Archive item">
                                <Archive className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => deleteMutation.mutate(item.id)} data-testid={`button-delete-catalog-${item.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === "packages" && (
          <>
            <ViewOnlyBanner area="furnishing" />
            <PackagesTab
            packages={packages}
            catalogItems={items}
            canEdit={canEditFurnishing}
            onEdit={(pkg) => {
              setEditingPackage(pkg);
              packageForm.reset({ name: pkg.name, roomType: pkg.roomType as typeof FURNISHING_ROOM_TYPES[number] });
              setPackageDialogOpen(true);
            }}
            onDelete={(id) => deletePackageMutation.mutate(id)}
            expandedPackageId={expandedPackageId}
            setExpandedPackageId={setExpandedPackageId}
            addItemToPackageId={addItemToPackageId}
            setAddItemToPackageId={setAddItemToPackageId}
            addItemCatalogId={addItemCatalogId}
            setAddItemCatalogId={setAddItemCatalogId}
            addItemQty={addItemQty}
            setAddItemQty={setAddItemQty}
            roomTypeLabels={ROOM_TYPE_LABELS}
          />
          </>
        )}

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingItem(null); form.reset(); } }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-catalog-item">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Edit Catalog Item" : "Add Catalog Item"}</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., Queen Bed Frame" data-testid="input-catalog-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="roomType" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Room Type *</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="select-catalog-room-type">
                            <SelectValue placeholder="Select room type" />
                          </SelectTrigger>
                          <SelectContent>
                            {FURNISHING_ROOM_TYPES.map((rt) => (
                              <SelectItem key={rt} value={rt}>{ROOM_TYPE_LABELS[rt]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="category" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="select-catalog-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FURNISHING_ITEM_CATEGORIES.map((cat) => (
                              <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} value={field.value ?? ""} rows={3} placeholder="Optional description..." data-testid="textarea-catalog-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="defaultPrice" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Price ($)</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ""} placeholder="e.g., 149.99" data-testid="input-catalog-price" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  {editingItem?.isSeeded ? (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                        Purchase URL
                        <Lock className="h-3 w-3 text-gray-400" />
                      </label>
                      {editingItem.affiliateLink || editingItem.purchaseUrl ? (
                        <a
                          href={editingItem.affiliateLink || editingItem.purchaseUrl || ""}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:underline truncate"
                          data-testid="link-catalog-url-readonly"
                        >
                          <ShoppingCart className="h-3 w-3 flex-shrink-0" />
                          Open purchase link
                        </a>
                      ) : (
                        <p className="text-sm text-gray-400 italic" data-testid="text-catalog-url-readonly">No link</p>
                      )}
                      <p className="text-xs text-gray-400">Link is managed by PadPulse and cannot be edited.</p>
                    </div>
                  ) : (
                    <FormField control={form.control} name="purchaseUrl" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Purchase URL</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value ?? ""} placeholder="https://..." data-testid="input-catalog-url" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel-catalog">Cancel</Button>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-save-catalog">
                    {editingItem ? "Save Changes" : "Add to Catalog"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <Dialog open={packageDialogOpen} onOpenChange={(open) => { setPackageDialogOpen(open); if (!open) { setEditingPackage(null); packageForm.reset(); } }}>
          <DialogContent className="max-w-md" data-testid="dialog-package">
            <DialogHeader>
              <DialogTitle>{editingPackage ? "Edit Package" : "New Package"}</DialogTitle>
            </DialogHeader>
            <Form {...packageForm}>
              <form onSubmit={packageForm.handleSubmit(onPackageSubmit)} className="space-y-4">
                <FormField control={packageForm.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Package Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder='e.g., "Standard Bedroom"' data-testid="input-package-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={packageForm.control} name="roomType" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Room Type *</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger data-testid="select-package-room-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FURNISHING_ROOM_TYPES.map((rt) => (
                            <SelectItem key={rt} value={rt}>{ROOM_TYPE_LABELS[rt]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setPackageDialogOpen(false)} data-testid="button-cancel-package">Cancel</Button>
                  <Button type="submit" disabled={createPackageMutation.isPending || updatePackageMutation.isPending} data-testid="button-save-package">
                    {editingPackage ? "Save" : "Create Package"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={importModalOpen}
          onOpenChange={(open) => {
            if (bulkImportMutation.isPending || isValidatingCsv) return;
            setImportModalOpen(open);
            if (!open) { setParsedRows([]); setImportSummary(null); setImportPhase("preview"); setImportMode("insert"); setIsValidatingCsv(false); }
          }}
        >
          <DialogContent
            className="max-w-5xl max-h-[90vh] flex flex-col"
            data-testid="dialog-csv-import"
            onEscapeKeyDown={(e) => { if (bulkImportMutation.isPending || isValidatingCsv) e.preventDefault(); }}
            onInteractOutside={(e) => { if (bulkImportMutation.isPending || isValidatingCsv) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (bulkImportMutation.isPending || isValidatingCsv) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle>Import from CSV</DialogTitle>
            </DialogHeader>

            {importPhase === "preview" && (
              <>
                <div className="border rounded-lg p-3 mb-3 bg-gray-50">
                  <div className="text-sm font-medium text-gray-700 mb-2">When a row's name matches an existing item:</div>
                  <div className="flex flex-col gap-1.5">
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer" data-testid="label-import-mode-insert">
                      <input
                        type="radio"
                        name="import-mode"
                        value="insert"
                        checked={importMode === "insert"}
                        onChange={() => setImportMode("insert")}
                        className="mt-0.5"
                        data-testid="radio-import-mode-insert"
                      />
                      <span>
                        <span className="font-medium">Insert only</span> — skip duplicates and only add new items
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer" data-testid="label-import-mode-upsert">
                      <input
                        type="radio"
                        name="import-mode"
                        value="upsert"
                        checked={importMode === "upsert"}
                        onChange={() => setImportMode("upsert")}
                        className="mt-0.5"
                        data-testid="radio-import-mode-upsert"
                      />
                      <span>
                        <span className="font-medium">Insert &amp; update</span> — merge on name, updating existing items
                      </span>
                    </label>
                  </div>
                </div>

                {isValidatingCsv && (
                  <div className="text-sm text-gray-600 mb-3 flex items-center gap-2" data-testid="text-import-validating">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Validating against latest catalog…</span>
                  </div>
                )}

                <div className={`text-sm text-gray-600 mb-3 ${isValidatingCsv ? "opacity-50" : ""}`}>
                  <span className="font-medium">{parsedRows.length} rows parsed</span>
                  {" • "}
                  <span className="text-green-700 font-medium" data-testid="text-import-count-new">{newRowCount} new</span>
                  {" • "}
                  <span className={`${importMode === "upsert" ? "text-blue-700" : "text-yellow-700"} font-medium`} data-testid="text-import-count-matching">
                    {matchingExistingCount} {importMode === "upsert" ? "to update" : "duplicate of existing"}
                  </span>
                  {" • "}
                  <span className="text-red-600 font-medium">{errorRowCount} with errors</span>
                  {duplicateInFileCount > 0 && (
                    <>
                      {" • "}
                      <span className="text-yellow-700 font-medium">{duplicateInFileCount} duplicate in file</span>
                    </>
                  )}
                </div>

                <div className="overflow-auto flex-1 border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium text-gray-600 w-10">#</th>
                        <th className="text-left p-2 font-medium text-gray-600">name</th>
                        <th className="text-left p-2 font-medium text-gray-600">room_type</th>
                        <th className="text-left p-2 font-medium text-gray-600">category</th>
                        <th className="text-left p-2 font-medium text-gray-600">description</th>
                        <th className="text-left p-2 font-medium text-gray-600">price</th>
                        <th className="text-left p-2 font-medium text-gray-600">purchase_url</th>
                        <th className="text-left p-2 font-medium text-gray-600">status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row) => {
                        const hasError = Object.keys(row.errors).length > 0;
                        const badge = classifyImportRow(row, importMode);
                        const willUpdate = badge === "willUpdate";
                        const willSkipAsDup = badge === "duplicate";
                        const isNew = badge === "new";
                        const rowClass = hasError
                          ? "bg-red-50"
                          : willUpdate
                          ? "bg-blue-50"
                          : willSkipAsDup
                          ? "bg-yellow-50"
                          : "";

                        const CellVal = ({ field, value }: { field: string; value: string }) => (
                          <td className={`p-2 ${row.errors[field] ? "text-red-700" : "text-gray-700"}`} data-testid={row.errors[field] ? `text-import-error-${row.rowNumber}-${field}` : undefined}>
                            <div>{value || <span className="text-gray-400 italic">—</span>}</div>
                            {row.errors[field] && <div className="text-red-600 text-xs mt-0.5">{row.errors[field]}</div>}
                          </td>
                        );

                        return (
                          <tr key={row.rowNumber} className={`border-b last:border-0 ${rowClass}`} data-testid={`row-import-preview-${row.rowNumber}`}>
                            <td className="p-2 text-gray-400">{row.rowNumber}</td>
                            <CellVal field="name" value={row.normalized.name} />
                            <CellVal field="room_type" value={row.normalized.roomType} />
                            <CellVal field="category" value={row.normalized.category} />
                            <CellVal field="description" value={row.normalized.description} />
                            <CellVal field="price" value={row.normalized.price} />
                            <CellVal field="purchase_url" value={row.normalized.purchaseUrl} />
                            <td className="p-2">
                              {isNew && <Badge className="bg-green-100 text-green-800 text-xs" variant="secondary" data-testid={`badge-status-${row.rowNumber}`}>Ready</Badge>}
                              {willUpdate && <Badge className="bg-blue-100 text-blue-800 text-xs" variant="secondary" data-testid={`badge-status-${row.rowNumber}`}>Will update</Badge>}
                              {willSkipAsDup && <Badge className="bg-yellow-100 text-yellow-800 text-xs" variant="secondary" data-testid={`badge-status-${row.rowNumber}`}>Duplicate</Badge>}
                              {hasError && <Badge className="bg-red-100 text-red-800 text-xs" variant="secondary" data-testid={`badge-status-${row.rowNumber}`}>Error</Badge>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end gap-2 pt-3">
                  <Button variant="outline" onClick={() => setImportModalOpen(false)} data-testid="button-cancel-import">
                    Cancel
                  </Button>
                  <Button
                    disabled={importableCount === 0 || bulkImportMutation.isPending || isValidatingCsv}
                    onClick={() => bulkImportMutation.mutate({ rows: parsedRows, mode: importMode })}
                    data-testid="button-confirm-import"
                  >
                    {isValidatingCsv
                      ? "Validating…"
                      : bulkImportMutation.isPending
                      ? "Importing..."
                      : importMode === "upsert"
                      ? `Confirm import (${newRowCount} new, ${matchingExistingCount} update${matchingExistingCount !== 1 ? "s" : ""})`
                      : `Confirm import (${newRowCount} item${newRowCount !== 1 ? "s" : ""})`}
                  </Button>
                </div>
              </>
            )}

            {importPhase === "summary" && importSummary && (
              <div className="flex-1 overflow-auto">
                <div className="text-sm text-gray-700 mb-4" data-testid="text-import-summary">
                  <p className="font-semibold text-base mb-1">
                    <span data-testid="text-summary-inserted">{importSummary.inserted} inserted</span>
                    {", "}
                    <span data-testid="text-summary-updated">{importSummary.updated} updated</span>
                    {", "}
                    <span data-testid="text-summary-skipped">{importSummary.skipped.length} skipped</span>
                    {importSummary.skipped.length > 0 && <span> — see details below.</span>}
                  </p>
                </div>
                {importSummary.skipped.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-2 font-medium text-gray-600">Row</th>
                          <th className="text-left p-2 font-medium text-gray-600">Name</th>
                          <th className="text-left p-2 font-medium text-gray-600">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importSummary.skipped.map((s) => (
                          <tr key={s.row} className="border-b last:border-0 bg-red-50">
                            <td className="p-2 text-gray-500">{s.row}</td>
                            <td className="p-2 text-gray-700">{s.name || "—"}</td>
                            <td className="p-2 text-red-700">{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex justify-end pt-4">
                  <Button onClick={() => setImportModalOpen(false)}>Done</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

function InlineFieldEditPopover({
  label,
  type,
  placeholder,
  initialValue,
  onSave,
  saving,
  testIdPrefix,
  children,
}: {
  label: string;
  type: "price" | "url" | "qty";
  placeholder?: string;
  initialValue: string;
  onSave: (value: string) => void;
  saving: boolean;
  testIdPrefix: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setValue(initialValue);
      setError(null);
    }
  };

  const handleSave = () => {
    const trimmed = value.trim();
    if (type === "price" && trimmed) {
      if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
        setError("Enter a valid price (e.g. 19.99)");
        return;
      }
    }
    if (type === "url" && trimmed) {
      try {
        const u = new URL(trimmed);
        if (u.protocol !== "http:" && u.protocol !== "https:") throw new Error();
      } catch {
        setError("Enter a valid URL starting with http(s)://");
        return;
      }
    }
    if (type === "qty") {
      if (!/^\d+$/.test(trimmed) || parseInt(trimmed, 10) < 1) {
        setError("Enter a whole number of 1 or more");
        return;
      }
    }
    onSave(trimmed);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group inline-flex items-center gap-1 rounded px-1 -mx-1 py-0.5 text-left hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-300"
          data-testid={`button-edit-${testIdPrefix}`}
          title={`Edit ${label.toLowerCase()}`}
        >
          {children}
          <Pencil className="h-3 w-3 text-gray-300 group-hover:text-gray-600" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 block">{label}</label>
          <Input
            autoFocus
            type={type === "qty" ? "number" : "text"}
            min={type === "qty" ? 1 : undefined}
            step={type === "qty" ? 1 : undefined}
            value={value}
            placeholder={placeholder}
            onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); handleSave(); }
              if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
            }}
            className="h-8 text-sm"
            data-testid={`input-edit-${testIdPrefix}`}
          />
          {error && <p className="text-xs text-red-600" data-testid={`error-edit-${testIdPrefix}`}>{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="outline" className="h-7" onClick={() => setOpen(false)} data-testid={`button-cancel-edit-${testIdPrefix}`}>Cancel</Button>
            <Button size="sm" className="h-7" disabled={saving} onClick={handleSave} data-testid={`button-save-edit-${testIdPrefix}`}>Save</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PackagesTab({
  packages,
  catalogItems,
  canEdit,
  onEdit,
  onDelete,
  expandedPackageId,
  setExpandedPackageId,
  addItemToPackageId,
  setAddItemToPackageId,
  addItemCatalogId,
  setAddItemCatalogId,
  addItemQty,
  setAddItemQty,
  roomTypeLabels,
}: {
  packages: FurnishingPackageWithCreator[];
  catalogItems: FurnishingCatalogItem[];
  canEdit: boolean;
  onEdit: (pkg: FurnishingPackage) => void;
  onDelete: (id: number) => void;
  expandedPackageId: number | null;
  setExpandedPackageId: (id: number | null) => void;
  addItemToPackageId: number | null;
  setAddItemToPackageId: (id: number | null) => void;
  addItemCatalogId: string;
  setAddItemCatalogId: (id: string) => void;
  addItemQty: number;
  setAddItemQty: (q: number) => void;
  roomTypeLabels: Record<string, string>;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const packageItemsQueries = useQuery<Record<number, FurnishingPackageItem[]>>({
    queryKey: ["/api/app/furnishing-packages", expandedPackageId, "items"],
    queryFn: async () => {
      if (!expandedPackageId) return {};
      const res = await fetch(`/api/app/furnishing-packages/${expandedPackageId}/items`, { credentials: "include" });
      if (!res.ok) return {};
      const data = await res.json();
      return { [expandedPackageId]: data };
    },
    enabled: !!expandedPackageId,
  });

  const packageItems: FurnishingPackageItem[] = expandedPackageId
    ? (packageItemsQueries.data?.[expandedPackageId] || [])
    : [];

  const addPackageItemMutation = useMutation({
    mutationFn: async ({ packageId, catalogItemId, quantity }: { packageId: number; catalogItemId: number; quantity: number }) => {
      const res = await apiRequest("POST", `/api/app/furnishing-packages/${packageId}/items`, { catalogItemId, quantity });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-packages", expandedPackageId, "items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-packages"] });
      setAddItemToPackageId(null);
      setAddItemCatalogId("");
      setAddItemQty(1);
      toast({ title: "Item added to package" });
    },
    onError: (err) => toast({ title: "Failed to add item", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const removePackageItemMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/furnishing-package-items/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-packages", expandedPackageId, "items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-packages"] });
      toast({ title: "Item removed from package" });
    },
    onError: (err) => toast({ title: "Failed to remove item", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const updatePackageItemQtyMutation = useMutation({
    mutationFn: async ({ id, quantity }: { id: number; quantity: number }) => {
      const res = await apiRequest("PATCH", `/api/app/furnishing-package-items/${id}`, { quantity });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-packages", expandedPackageId, "items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-packages"] });
    },
    onError: (err) => toast({ title: "Failed to update quantity", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const updateCatalogItemInlineMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { defaultPrice?: string | null; purchaseUrl?: string | null } }) => {
      const res = await apiRequest("PATCH", `/api/app/furnishing-catalog/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-catalog"] });
      toast({ title: "Item updated" });
    },
    onError: (err) => toast({ title: "Failed to update item", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const catalogMap: Record<number, FurnishingCatalogItem> = {};
  for (const c of catalogItems) catalogMap[c.id] = c;

  // Estimates are derived from `pkg.items`, which the
  // GET /api/app/furnishing-packages endpoint already inlines —
  // no extra client requests are made (Task #1674). For the
  // currently expanded package we prefer the dedicated items query
  // so optimistic add/remove/qty updates appear immediately.
  const packageEstimates: Record<number, { total: number; missingCount: number; hasItems: boolean }> = {};
  for (const pkg of packages) {
    const source =
      pkg.id === expandedPackageId && packageItemsQueries.data?.[pkg.id]
        ? packageItemsQueries.data[pkg.id]
        : pkg.items || [];
    let total = 0;
    let missingCount = 0;
    for (const pi of source) {
      const cat = catalogMap[pi.catalogItemId];
      const price = parsePrice(cat?.defaultPrice);
      if (price === null) {
        missingCount += 1;
      } else {
        total += price * pi.quantity;
      }
    }
    packageEstimates[pkg.id] = {
      total,
      missingCount,
      hasItems: source.length > 0,
    };
  }

  if (packages.length === 0) {
    return (
      <div className="text-center py-16 border rounded-lg">
        <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">No packages yet</p>
        <p className="text-sm text-gray-400 mt-1">Create named bundles of catalog items to apply to rooms at once</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {packages.map((pkg) => {
        const isExpanded = expandedPackageId === pkg.id;
        return (
          <div key={pkg.id} className="border rounded-lg overflow-hidden" data-testid={`package-card-${pkg.id}`}>
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b">
              <div className="flex items-center gap-3">
                <Package className="h-4 w-4 text-gray-400" />
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{pkg.name}</h3>
                  <span className="text-xs text-gray-500">{roomTypeLabels[pkg.roomType] || pkg.roomType}</span>
                  <p className="text-xs text-gray-400 mt-0.5" data-testid={`text-package-added-by-${pkg.id}`}>
                    Added by {pkg.createdByName || "Unknown"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(() => {
                  const est = packageEstimates[pkg.id];
                  if (!est) return null;
                  if (!est.hasItems) {
                    return (
                      <span
                        className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-400"
                        data-testid={`badge-package-estimate-${pkg.id}`}
                      >
                        No items
                      </span>
                    );
                  }
                  const incomplete = est.missingCount > 0;
                  return (
                    <span
                      className={
                        incomplete
                          ? "text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500"
                          : "text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-medium"
                      }
                      title={
                        incomplete
                          ? `${est.missingCount} item${est.missingCount === 1 ? "" : "s"} missing price`
                          : "Estimated package total"
                      }
                      data-testid={`badge-package-estimate-${pkg.id}`}
                    >
                      {incomplete ? "~" : "Est. "}
                      {fmtCurrency(est.total)}
                      {incomplete && (
                        <span className="ml-1 text-gray-400">
                          · {est.missingCount} {est.missingCount === 1 ? "item" : "items"} missing price
                        </span>
                      )}
                    </span>
                  );
                })()}
                <Button size="sm" variant="ghost" onClick={() => setExpandedPackageId(isExpanded ? null : pkg.id)} data-testid={`button-expand-package-${pkg.id}`}>
                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {isExpanded ? "Hide" : "Edit Items"}
                </Button>
                {canEdit && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => onEdit(pkg)} data-testid={`button-edit-package-${pkg.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => onDelete(pkg.id)} data-testid={`button-delete-package-${pkg.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {isExpanded && (
              <div className="p-4">
                {packageItemsQueries.isLoading ? (
                  <p className="text-sm text-gray-400">Loading items...</p>
                ) : packageItems.length === 0 ? (
                  <p className="text-sm text-gray-400 mb-3">No items in this package yet.</p>
                ) : (
                  <table className="w-full text-sm mb-3">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-2 font-medium text-gray-600">Item</th>
                        <th className="text-left p-2 font-medium text-gray-600">Category</th>
                        <th className="text-left p-2 font-medium text-gray-600">Link</th>
                        <th className="text-left p-2 font-medium text-gray-600">Price</th>
                        <th className="text-left p-2 font-medium text-gray-600">Qty</th>
                        <th className="text-left p-2 font-medium text-gray-600">Line Total</th>
                        <th className="text-left p-2 font-medium text-gray-600">Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packageItems.map((pi) => {
                        const cat = catalogMap[pi.catalogItemId];
                        const price = parsePrice(cat?.defaultPrice);
                        const priceValid = price !== null;
                        const lineTotal = priceValid ? price! * pi.quantity : null;
                        return (
                          <tr key={pi.id} className="border-b last:border-0" data-testid={`package-item-${pi.id}`}>
                            <td className="p-2 text-gray-900">{cat ? cat.name : `Item #${pi.catalogItemId}`}</td>
                            <td className="p-2">
                              {cat ? (
                                <Badge
                                  className={CATEGORY_COLORS[cat.category] || "bg-gray-100 text-gray-800"}
                                  variant="secondary"
                                  data-testid={`badge-package-item-category-${pi.id}`}
                                >
                                  {CATEGORY_LABELS[cat.category] || cat.category}
                                </Badge>
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="p-2" data-testid={`link-package-item-${pi.id}`}>
                              {cat ? (
                                <InlineFieldEditPopover
                                  label="Purchase link"
                                  type="url"
                                  placeholder="https://..."
                                  initialValue={cat.purchaseUrl ?? ""}
                                  onSave={(val) => updateCatalogItemInlineMutation.mutate({ id: cat.id, data: { purchaseUrl: val.trim() || null } })}
                                  saving={updateCatalogItemInlineMutation.isPending}
                                  testIdPrefix={`package-item-link-${pi.id}`}
                                >
                                  {cat.purchaseUrl ? (
                                    <span className="inline-flex items-center gap-1 text-blue-600 text-xs">
                                      Open <ExternalLink className="h-3 w-3" />
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 text-xs">— Add link</span>
                                  )}
                                </InlineFieldEditPopover>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="p-2 text-gray-700" data-testid={`text-package-item-price-${pi.id}`}>
                              {cat ? (
                                <InlineFieldEditPopover
                                  label="Price"
                                  type="price"
                                  placeholder="0.00"
                                  initialValue={cat.defaultPrice ?? ""}
                                  onSave={(val) => updateCatalogItemInlineMutation.mutate({ id: cat.id, data: { defaultPrice: val.trim() || null } })}
                                  saving={updateCatalogItemInlineMutation.isPending}
                                  testIdPrefix={`package-item-price-${pi.id}`}
                                >
                                  {priceValid ? <span>{fmtCurrency(price!)}</span> : <span className="text-gray-400">— Add price</span>}
                                </InlineFieldEditPopover>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="p-2 text-gray-700" data-testid={`text-package-item-qty-${pi.id}`}>
                              {canEdit ? (
                                <InlineFieldEditPopover
                                  label="Quantity"
                                  type="qty"
                                  placeholder="1"
                                  initialValue={String(pi.quantity)}
                                  onSave={(val) => {
                                    const qty = parseInt(val, 10);
                                    if (qty !== pi.quantity) updatePackageItemQtyMutation.mutate({ id: pi.id, quantity: qty });
                                  }}
                                  saving={updatePackageItemQtyMutation.isPending}
                                  testIdPrefix={`package-item-qty-${pi.id}`}
                                >
                                  <span>{pi.quantity}</span>
                                </InlineFieldEditPopover>
                              ) : (
                                <span>{pi.quantity}</span>
                              )}
                            </td>
                            <td className="p-2 text-gray-700" data-testid={`text-package-item-line-total-${pi.id}`}>
                              {lineTotal !== null ? fmtCurrency(lineTotal) : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="p-2">
                              {canEdit && (
                                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-6 w-6 p-0" onClick={() => removePackageItemMutation.mutate(pi.id)} data-testid={`button-remove-package-item-${pi.id}`}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="border-t bg-gray-50">
                      <tr>
                        {(() => {
                          const itemsWithPrice = packageItems.filter((pi) => {
                            const cat = catalogMap[pi.catalogItemId];
                            return parsePrice(cat?.defaultPrice) !== null;
                          });
                          const hasMissingPrices = itemsWithPrice.length < packageItems.length;
                          const grandTotal = itemsWithPrice.reduce((sum, pi) => {
                            const cat = catalogMap[pi.catalogItemId];
                            return sum + parsePrice(cat!.defaultPrice)! * pi.quantity;
                          }, 0);
                          return (
                            <>
                              <td colSpan={5} className="p-2 text-right font-medium text-gray-700">
                                Package total
                              </td>
                              <td className="p-2 font-semibold text-gray-900" colSpan={2} data-testid={`text-package-total-${pkg.id}`}>
                                {fmtCurrency(grandTotal)}
                                {hasMissingPrices && (
                                  <span className="ml-1 text-xs font-normal text-gray-400">(excludes items without a price)</span>
                                )}
                              </td>
                            </>
                          );
                        })()}
                      </tr>
                    </tfoot>
                  </table>
                )}

                {!canEdit ? null : addItemToPackageId === pkg.id ? (
                  <div className="flex items-end gap-2 mt-2">
                    <div className="flex-1">
                      <label className="text-xs text-gray-600 mb-1 block">Catalog Item</label>
                      <Select value={addItemCatalogId} onValueChange={setAddItemCatalogId}>
                        <SelectTrigger className="h-8 text-sm" data-testid="select-package-add-item">
                          <SelectValue placeholder="Select item..." />
                        </SelectTrigger>
                        <SelectContent>
                          {catalogItems.filter((c) => c.roomType === pkg.roomType && c.userId === pkg.userId).map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-20">
                      <label className="text-xs text-gray-600 mb-1 block">Qty</label>
                      <Input type="number" min={1} value={addItemQty} onChange={(e) => setAddItemQty(parseInt(e.target.value) || 1)} className="h-8 text-sm" data-testid="input-package-item-qty" />
                    </div>
                    <Button size="sm" disabled={!addItemCatalogId || addPackageItemMutation.isPending} onClick={() => addPackageItemMutation.mutate({ packageId: pkg.id, catalogItemId: parseInt(addItemCatalogId), quantity: addItemQty })} data-testid="button-confirm-add-package-item">
                      Add
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setAddItemToPackageId(null); setAddItemCatalogId(""); setAddItemQty(1); }} data-testid="button-cancel-add-package-item">
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setAddItemToPackageId(pkg.id)} data-testid={`button-add-item-to-package-${pkg.id}`}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Item
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
